/**
 * The one place the client talks to the server. Every call carries the bearer token, every failure comes back
 * as an ApiFailure carrying the server's own code and message — the screens show that message rather than
 * inventing their own, so an operator is never told something different from what the record says (ERR-001).
 */
import type { Reconciliation, ToleranceSpec } from "@corpex-ebr/domain";
import type { AuditPage } from "./audit.js";
import type { SessionPolicy } from "./idle-lock.js";
import { offlineQueueFor, replicaAnswerFor, setReachable } from "./offline/connectivity.js";
import { submitOnce } from "./submission.js";

export interface ApiFailure {
  code: string;
  message: string;
  correlationId?: string;
  details?: unknown;
  status: number;
}

export class ApiError extends Error {
  constructor(readonly failure: ApiFailure) {
    super(failure.message);
    this.name = "ApiError";
  }
}

/** The state machine's own refusal code, when the server's `details` carries one (e.g. FORBIDDEN_TRANSITION). */
function guardCodeOf(details: unknown): string | undefined {
  if (typeof details !== "object" || details === null) return undefined;
  const code = (details as Record<string, unknown>)["guardCode"];
  return typeof code === "string" ? code : undefined;
}

/** A server refusal as a sentence the operator can act on, never a bare code (ERR-001). */
export function refusalMessage(e: unknown): string {
  if (!(e instanceof ApiError)) return "The request could not be completed.";
  const guardCode = guardCodeOf(e.failure.details);
  return guardCode ? `${e.failure.message} (${guardCode}).` : e.failure.message;
}

/**
 * The held access token.
 *
 * Kept in session storage rather than only in memory, so a refresh or a deep link into a record does not
 * throw the operator back to sign-in mid-batch. Session storage rather than local storage is the deliberate
 * middle: it survives a reload of this tab and dies when the tab closes, so a shared floor terminal does
 * not carry one person's session into the next person's shift.
 *
 * A token in browser storage is reachable by script, which is the accepted trade for a public client with
 * no backend-for-frontend. What limits the damage is that it is short-lived and that every act it can
 * perform is re-authorised server-side; a signature needs fresh re-authentication regardless (§5.6).
 */
const TOKEN_KEY = "ebr.token";

function readStoredToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    // a browser that refuses storage still works, it just signs in again on reload
    return null;
  }
}

let token: string | null = readStoredToken();

export const setToken = (t: string | null): void => {
  token = t;
  try {
    if (t) sessionStorage.setItem(TOKEN_KEY, t);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    // storage unavailable: the session simply lasts as long as this page does
  }
};

export const getToken = (): string | null => token;

/**
 * The event the shell listens for when the server stops accepting this session.
 *
 * A token that has expired is not a transient failure: every subsequent request will be refused too, so
 * leaving the person on a screen full of "could not be loaded" — with nothing to press — is a dead end.
 * The session is dropped and the sign-in screen comes back, which is the only recovery there is.
 */
export const SESSION_ENDED = "ebr:session-ended";

/**
 * SEC-030: the server refused a password-only token (401 MFA_REQUIRED). From then on this tab's sign-ins ask the
 * identity provider for the second-factor level, so the person is not sent round a password-only loop.
 */
export const SIGN_IN_ACR_KEY = "ebr.signInAcr";
export function requireSecondFactor(): void {
  sessionStorage.setItem(SIGN_IN_ACR_KEY, "silver");
}

/**
 * Where the API lives. Same origin in production, but not necessarily the same *path*: this client is
 * served under a prefix, and the API sits behind that same prefix. Defaulting to the host root sends every
 * request to the wrong place and yields a 404 from whatever else is published there — which is exactly
 * what happened before this was derived from the mount point rather than assumed.
 */
const BASE =
  (import.meta.env?.["VITE_API_BASE"] as string | undefined) ??
  (import.meta.env?.BASE_URL ?? "/").replace(/\/$/, "");

/** ERR-005: no answer at all. The screen says what is and is not known, and that pressing again is safe. */
const NETWORK_MESSAGE =
  "The server could not be reached, so it is not confirmed that this was saved. Check the connection and press the button again: the same action will not be recorded twice.";
/** ERR-012: a request that never gets an answer is abandoned, and the screen says so, instead of spinning for ever. */
const REQUEST_TIMEOUT_MS = 45_000;

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers: {
        ...(body === undefined ? {} : { "content-type": "application/json" }),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
  } catch {
    setReachable(false); // OFF-001: the next capture goes to the offline queue rather than being lost
    // URS-8.6-001: a read of a batch the device holds is answered from its replica, as of when it was cut
    const local = method === "GET" ? replicaAnswerFor(path) : undefined;
    if (local !== undefined) return local as T;
    throw new ApiError({ code: "NETWORK", message: NETWORK_MESSAGE, status: 0 });
  }
  setReachable(true);
  const text = await res.text();
  const parsed = text ? (JSON.parse(text) as Record<string, unknown>) : null;
  if (res.status === 401 && token !== null) {
    // The server has rejected the credential we hold. Drop it, and tell the shell to ask for a new one.
    if ((parsed?.["error"] as { code?: string } | undefined)?.code === "MFA_REQUIRED") requireSecondFactor();
    setToken(null);
    window.dispatchEvent(new Event(SESSION_ENDED));
  }
  if (!res.ok) {
    const e = (parsed?.["error"] ?? {}) as Partial<ApiFailure>;
    throw new ApiError({
      code: e.code ?? "UNKNOWN",
      message: e.message ?? `The server refused this request (${res.status}).`,
      status: res.status,
      ...(e.correlationId ? { correlationId: e.correlationId } : {}),
      ...(e.details === undefined ? {} : { details: e.details }),
    });
  }
  return parsed as T;
}

/**
 * OFF-001/OFF-010: while the server cannot be reached, a capture is queued on the device instead of failing.
 * The queue refuses what may not be done offline (signatures above all) with the reason, as an ApiError, so
 * the screen shows it exactly as it would show a server refusal.
 */
async function viaOfflineQueue<T>(
  commandType: string,
  batchId: string,
  body: Record<string, unknown>,
  send: () => Promise<T>,
): Promise<T> {
  const queue = offlineQueueFor();
  if (!queue) return send();
  try {
    return (await queue(commandType, batchId, body)) as T;
  } catch (e) {
    const code = (e as { code?: string }).code ?? "OFFLINE";
    throw new ApiError({ code, message: e instanceof Error ? e.message : "Not recorded.", status: 0 });
  }
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  /** ERR-009: a submission is made once; see submission.ts. */
  post: <T>(path: string, body?: unknown) =>
    submitOnce<T>(
      path,
      (body ?? {}) as Record<string, unknown>,
      (b) => request<T>("POST", path, b),
      hasNoVerdict,
    ).result,
  put: <T>(path: string, body: unknown) => request<T>("PUT", path, body),
};

/** No response, or a server fault: the person cannot know whether the act was recorded (ERR-005). */
function hasNoVerdict(e: unknown): boolean {
  return (
    e instanceof ApiError && (e.failure.status === 0 || e.failure.status >= 500 || e.failure.status === 408)
  );
}

/** A command id makes a repeated submission idempotent rather than a second record (CONC-002). */
export const commandId = (): string => crypto.randomUUID();

// ------------------------------------------------------------------ shapes the screens read

export interface Batch {
  id: string;
  batch_number: string;
  status: string;
  planned_qty: string;
  mbr_snapshot_id: string | null;
  hold_reason: string | null;
  created_at_server: string;
  started_at_server: string | null;
}

export interface StepState {
  batch_id: string;
  step_execution_id: string;
  mbr_step_id: string;
  instance_ordinal: number;
  state: string;
  started_at_server: string | null;
  completed_at_server: string | null;
  current_values: string;
  out_of_limit_values: string;
  corrections: string;
  /** WF-006: the step sits on a DECISION branch the recipe did not take; it is not outstanding. */
  branch_not_taken?: boolean;
  /** WF-023: everyone who acted on this execution (a step can span a shift change). */
  performers?: { user_id: string; name: string }[];
}

/** The latest execution of each step: a repeat (WF-009) supersedes the one before it for progress, never in the record. */
export function latestExecutions(rows: StepState[]): StepState[] {
  const byStep = new Map<string, StepState>();
  for (const r of rows) {
    const prior = byStep.get(r.mbr_step_id);
    if (!prior || r.instance_ordinal >= prior.instance_ordinal) byStep.set(r.mbr_step_id, r);
  }
  return [...byStep.values()];
}

export interface DataPoint {
  id: string;
  mbr_step_id: string;
  mbr_parameter_id: string;
  value_numeric: string | null;
  /** DI-018: the number at its parameter's declared precision, with its unit; DI-004: codes spelled out. */
  display_value?: string | number | boolean | null;
  uom_code?: string | null;
  spec_evaluation_label?: string;
  entry_method_label?: string;
  value_text: string | null;
  value_bool: boolean | null;
  spec_evaluation: string;
  entry_method: string;
  source_ref: string | null;
  is_current: boolean;
  supersedes_data_point_id: string | null;
  correction_reason_code: string | null;
  correction_reason_text: string | null;
  server_timestamp: string;
  /** URS-8.4-003: the device's own clock, evidence only — shown labelled "reported by device, not verified". */
  device_timestamp?: string | null;
  /** PRT-019: an entry captured offline carries its flag and the interval the server bounded it to. */
  captured_offline?: boolean;
  occurred_not_before?: string | null;
  occurred_not_after?: string | null;
  entered_by: string;
  on_behalf_of: string | null;
  event_id: string;
}

/** GET /batches/:id/signing-context: the subject exactly as the server will re-resolve it when signing. */
export interface SigningContext {
  subjectType: string;
  subjectId: string;
  batchId: string;
  batchNumber: string;
  ref: string;
  state: unknown;
  version: string;
  signable: boolean;
  notSignableReason: string | null;
  missingFields: string[];
  performerUserId: string | null;
  allowedMeanings: string[];
  required: string[];
  signer: {
    fullName: string;
    username: string;
    role: string | null;
    /** The role that authorises each meaning this subject accepts (#38). */
    roleByMeaning?: Record<string, string | null>;
  };
}

/** One applied signature (GET /batches/:id/signatures). */
export interface SignatureRecord {
  id: string;
  subject_type: string;
  subject_id: string;
  meaning: string;
  signer_printed_name: string;
  signer_username: string;
  signer_role: string | null;
  signed_at_server: string;
  site_tz: string;
  manifestation_text: string;
  is_current: boolean;
}

/** GET /batches/:id/signature-requirements: what the batch owes, matching the release gate. */
export interface SignatureRequirement {
  subject_type: string;
  subject_id: string;
  mbr_step_id: string | null;
  meaning: string;
  signature_id: string | null;
  signer_printed_name: string | null;
  signer_username: string | null;
  signed_at_server: string | null;
  site_tz: string | null;
  manifestation_text: string | null;
}

export interface Me {
  id: string;
  username: string;
  fullName: string;
  /** The site's IANA zone; a typed date-time with no offset is read in it. */
  siteTz?: string;
  permissions: string[];
}

// ------------------------------------------------------------------ weigh & dispense (dispense.controller.ts)

export type DispenseMode = "GROSS" | "NET" | "NEGATIVE";
export type DispenseStatus =
  | "PENDING"
  | "IN_PROGRESS"
  | "DISPENSED"
  | "VERIFIED"
  | "ISSUED"
  | "REJECTED"
  | "CANCELLED";

/** A row of `dispense.service.ts#list`: the order's own columns plus the joins the table needs. */
export interface DispenseOrder {
  id: string;
  batch_id: string;
  mbr_step_id: string;
  material_definition_id: string;
  material_lot_id: string | null;
  device_id: string | null;
  mode: DispenseMode;
  nominal_qty: string;
  target_qty: string | null;
  uom_id: string;
  tolerance_spec: ToleranceSpec;
  status: DispenseStatus;
  performed_by: string | null;
  verified_by: string | null;
  material_code: string;
  material_name: string;
  lot_number: string | null;
  device_code: string | null;
  dispensed_qty: string | null;
  container_count: string | null;
}

/** A row of `dispense.service.ts#containers`: one physical container, current or superseded. */
export interface DispenseContainer {
  id: string;
  dispense_order_id: string;
  container_code: string;
  sequence_no: number;
  device_id: string;
  mode: DispenseMode;
  tare_qty: string | null;
  gross_qty: string | null;
  before_qty: string | null;
  after_qty: string | null;
  net_qty: string;
  uom_id: string;
  tolerance_verdict: "IN_TOLERANCE" | "SOFT_EXCEEDED" | "HARD_EXCEEDED";
  capture_method: "DEVICE" | "MANUAL";
  supersedes_container_id: string | null;
  superseded_reason: string | null;
  is_current: boolean;
  performed_by: string;
  performed_by_name: string;
  captured_at_server: string;
  /** Highest copy number printed so far, as text; null when never printed. */
  labels_printed: string | null;
}

export interface DispenseReconciliation extends Reconciliation {
  containerCount: number;
}

export interface LabelPrintResult {
  copyNumber: number;
  payloadSha256: string;
  delivered: { ok: boolean; error?: string };
}

// ------------------------------------------------------------------ review & release (release.service.ts)

export type ExceptionSeverity = "INFO" | "MINOR" | "MAJOR" | "CRITICAL";
export type ExceptionReviewState =
  | "OPEN"
  | "ACKNOWLEDGED"
  | "JUSTIFIED"
  | "ESCALATED_TO_DEVIATION"
  | "CLOSED";

/** `GET /exceptions/dashboard` (URS-EXC-004): open findings counted along each axis. */
export interface ExceptionDashboard {
  open: number;
  batches: { batchId: string; batchNumber: string; open: number; worst: ExceptionSeverity }[];
  byBatch: Record<string, number>;
  byArea: Record<string, number>;
  byProduct: Record<string, number>;
  byType: Record<string, number>;
  bySeverity: Record<string, number>;
  byAge: Record<string, number>;
}

/** `GET /batches/:id/exception-report` (URS-EXC-004): the exception-only batch review. */
export interface ExceptionReport {
  batch: { id: string; batch_number: string; status: string; product: string | null; area: string | null };
  gate: {
    releasable: boolean;
    open_exceptions: string;
    blocking_exceptions: string;
    open_deviations: string;
    missing_signatures: string;
  };
  auditPolicy: { criticality: string; scope: string; reachesQp: boolean };
  stepsTotal: number;
  stepsWithFindings: number;
  stepsClean: number;
  findings: {
    id: string;
    rule_id: string;
    category: string;
    severity: ExceptionSeverity;
    title: string;
    review_state: string;
    detected_at: string;
    mbr_step_id: string | null;
  }[];
  openDeviations: {
    id: string;
    deviation_number: string;
    title: string;
    classification: string;
    state: string;
  }[];
  reviews: { review_type: string; outcome: string; issues_found: boolean; reviewed_at_server: string }[];
}

/** A row of `release.service.ts#exceptions`: `exception_item.*` plus the joins the workbench needs. */
export interface ExceptionItem {
  id: string;
  batch_id: string;
  step_execution_id: string | null;
  data_point_id: string | null;
  source_event_id: string;
  category: string;
  severity: ExceptionSeverity;
  detected_at: string;
  rule_id: string;
  rule_version: number;
  title: string;
  detail: Record<string, unknown>;
  auto_dispositioned: boolean;
  review_state: ExceptionReviewState;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_comment: string | null;
  deviation_id: string | null;
  reviewed_by_name: string | null;
  deviation_number: string | null;
  mbr_step_id: string | null;
}

export type DeviationState = "OPEN" | "UNDER_INVESTIGATION" | "CLOSED_ACCEPTED" | "CLOSED_REJECTED";

/** A row of `release.service.ts#deviations`: `deviation.*` plus the two names the panel shows. */
export interface Deviation {
  id: string;
  batch_id: string;
  step_execution_id: string | null;
  deviation_number: string;
  title: string;
  description: string;
  classification: "MINOR" | "MAJOR" | "CRITICAL";
  state: DeviationState;
  qms_reference: string | null;
  root_cause: string | null;
  impact_assessment: string | null;
  capa_reference: string | null;
  product_impact: "NONE" | "POSSIBLE" | "CONFIRMED" | null;
  change_control_reference: string | null;
  raised_context: Record<string, unknown> | null;
  raised_by: string;
  raised_at_server: string;
  closed_by: string | null;
  closed_at_server: string | null;
  closure_reason: string | null;
  raised_by_name: string;
  closed_by_name: string | null;
}

/** A row of `release.service.ts#reviews` — a recorded review of a defined scope, with its proof hash. */
export interface BatchReview {
  id: string;
  review_type: "PRODUCTION" | "AUDIT_TRAIL" | "QA";
  outcome: "ACCEPTED" | "RETURNED_TO_PRODUCTION" | "REJECTED";
  comment: string | null;
  reviewed_at_server: string;
  manifest_sha256: string;
  scope: Record<string, unknown>;
  reviewed_by_name: string;
  /** WF-030: the reviewer's positive statement. Null only on reviews recorded before it was required. */
  issues_found: boolean | null;
}

/** The result of `POST /reviews` — the manifest hash lives in the appended event's own payload. */
export interface RecordReviewResult {
  event: {
    eventId: string;
    payload: { manifestSha256: string; scope: Record<string, unknown>; outcome: string; reviewType: string };
  };
  replayed: boolean;
}

/** `batch_release_gate` — every blocking condition §6.6 names, as the database computes it. */
export interface ReleaseGate {
  batch_id: string;
  batch_number: string;
  status: string;
  open_exceptions: string;
  blocking_exceptions: string;
  open_deviations: string;
  missing_signatures: string;
  lab_blockers: string;
  audit_trail_reviewed: boolean;
  /** Steps of the batch's own frozen recipe with no completed execution against them. */
  steps_total?: number | string;
  steps_outstanding?: number | string;
  releasable: boolean;
}

/** A row of `release.service.ts#dispositions` — a stored decision plus the gate as it stood at the time. */
export interface BatchDisposition {
  id: string;
  batch_id: string;
  event_id: string;
  plan_id: string | null;
  decision: "RELEASED" | "CONDITIONALLY_RELEASED" | "ON_HOLD" | "REJECTED" | "REWORKED" | "QUARANTINED";
  verdict_text: string;
  reason: string;
  market: string | null;
  gate_snapshot: ReleaseGate;
  decided_by: string;
  decided_at_server: string;
  signature_id: string | null;
  decided_by_name: string;
  /** WF-028: FULL review, or release on the strength of exception review alone. */
  review_path: "FULL" | "BY_EXCEPTION";
}

/** A master batch record template (GET /mbr/templates). */
export interface MbrTemplate {
  id: string;
  code: string;
  name: string;
  recipe_type: string;
  is_active: boolean;
  created_at_server: string;
  updated_at_server: string;
}

/** One version of a master batch record (GET /mbr/templates/:id/versions, GET /mbr/versions/:id). */
export interface MbrVersion {
  id: string;
  template_id: string;
  version_label: string;
  status: string;
  content_hash: string;
  author_user_id?: string;
  effective_from?: string | null;
  content?: import("@corpex-ebr/domain").MbrContent;
}
export interface MbrStepType {
  type: string;
  label: string;
  family: string;
  defaultSignaturePolicy?: string;
}
export interface MbrAnalysis {
  report: {
    errors: number;
    warnings: number;
    approvable: boolean;
    findings: { rule: string; severity: "ERROR" | "WARN"; message: string; stepId?: string }[];
  };
  stale: boolean;
}

/** A temporal limit set in force as of a moment (GET /reference/limits). */
export interface LimitVersion {
  id: string;
  parameter_code: string;
  uom_code?: string | null;
  min_spec?: string | null;
  max_spec?: string | null;
  min_action?: string | null;
  max_action?: string | null;
  min_alert?: string | null;
  max_alert?: string | null;
  target?: string | null;
  /** The API names the start of the period valid_from; effective_from is kept for older fixtures. */
  valid_from?: string | null;
  effective_from?: string | null;
  scope_type?: string;
  scope_id?: string;
  uom_id?: string;
  precision_places?: number;
  rounding_mode?: string;
  change_ref?: string | null;
}

/** A user account (GET /users). */
export interface UserAccount {
  id: string;
  username: string;
  full_name: string;
  employee_id?: string | null;
  email?: string | null;
  kind: string;
  status: string;
  identity_verified_at?: string | null;
  signature_ack_at?: string | null;
  created_at_server: string;
}

/** A role and the permissions it carries (GET /roles). */
export interface Role {
  code: string;
  name: string;
  description?: string | null;
  is_active: boolean;
  permissions: string[];
}

/** A weighing device and whether its daily check is current (GET /weighing-devices). */
export interface WeighingDevice {
  id: string;
  code: string;
  name: string;
  driver: string;
  capacity: string;
  readability: string;
  calibration_due?: string | null;
  qualification_due?: string | null;
  is_active: boolean;
  checkCurrent: boolean;
  minimumWeight?: string | null;
}

/** One standing grant, as the periodic access review reads it (GET /reports/access-review). */
export interface AccessReviewRow {
  user_id: string;
  username: string;
  full_name: string;
  kind: string;
  status: string;
  /** null for an account that holds no role right now: the view lists every account (LEFT JOIN). */
  role_code: string | null;
  site_path?: string | null;
  granted_at: string | null;
  valid_until?: string | null;
  granted_by?: string | null;
  approval_ref?: string | null;
  identity_verified_at?: string | null;
  signature_ack_at?: string | null;
}

export const endpoints = {
  me: () => api.get<Me>("/api/v1/me"),
  /** SEC-014: the inactivity-lock interval per workstation class. */
  sessionPolicy: () => api.get<SessionPolicy>("/api/v1/me/session-policy"),
  signingContext: (batchId: string, subjectType: string, subjectId: string) =>
    api.get<SigningContext>(
      `/api/v1/batches/${batchId}/signing-context?subjectType=${subjectType}&subjectId=${subjectId}`,
    ),
  signatures: (batchId: string, subjectType: string, subjectId: string) =>
    api.get<SignatureRecord[]>(
      `/api/v1/batches/${batchId}/signatures?subjectType=${subjectType}&subjectId=${subjectId}`,
    ),
  signatureRequirements: (batchId: string) =>
    api.get<SignatureRequirement[]>(`/api/v1/batches/${batchId}/signature-requirements`),
  applySignature: (batchId: string, body: Record<string, unknown>) =>
    api.post<SignatureRecord>(`/api/v1/batches/${batchId}/signatures`, body),
  batches: (status?: string) => api.get<Batch[]>(`/api/v1/batches${status ? `?status=${status}` : ""}`),
  batch: (id: string) => api.get<Batch>(`/api/v1/batches/${id}`),
  steps: (id: string) => api.get<StepState[]>(`/api/v1/batches/${id}/steps`),
  recipe: (id: string) =>
    api.get<{ content: unknown; snapshot: Record<string, unknown> }>(`/api/v1/batches/${id}/recipe`),
  values: (id: string, stepId?: string) =>
    api.get<DataPoint[]>(`/api/v1/batches/${id}/values${stepId ? `?stepId=${stepId}` : ""}`),
  stepAction: (id: string, body: Record<string, unknown>) =>
    viaOfflineQueue("StepAction", id, body, () => api.post(`/api/v1/batches/${id}/steps`, body)),
  recordValue: (id: string, body: Record<string, unknown>) =>
    viaOfflineQueue("RecordValue", id, body, () => api.post(`/api/v1/batches/${id}/values`, body)),
  correctValue: (id: string, body: Record<string, unknown>) =>
    api.post(`/api/v1/batches/${id}/values/corrections`, body),
  batchTransition: (id: string, body: Record<string, unknown>) =>
    api.post(`/api/v1/batches/${id}/transition`, body),

  /** BL-017: dispense orders across batches; `status` is a comma-separated list. */
  dispenseWorklist: (status?: string) =>
    api.get<DispenseOrder[]>(`/api/v1/dispense-orders${status ? `?status=${status}` : ""}`),
  productionBoard: () => api.get<unknown[]>("/api/v1/production-board"),
  batchRelations: (batchId: string) => api.get<unknown[]>(`/api/v1/batches/${batchId}/relations`),
  batchTicket: (batchId: string) => api.get<unknown>(`/api/v1/batches/${batchId}/ticket`),
  dispenseOrders: (batchId: string) => api.get<DispenseOrder[]>(`/api/v1/batches/${batchId}/dispense`),
  dispenseContainers: (orderId: string) =>
    api.get<DispenseContainer[]>(`/api/v1/dispense/${orderId}/containers`),
  dispenseReconciliation: (orderId: string) =>
    api.get<DispenseReconciliation>(`/api/v1/dispense/${orderId}/reconciliation`),
  weigh: (batchId: string, orderId: string, body: Record<string, unknown>) =>
    api.post(`/api/v1/batches/${batchId}/dispense/${orderId}/weighings`, body),
  printDispenseLabel: (batchId: string, body: Record<string, unknown>) =>
    api.post<LabelPrintResult>(`/api/v1/batches/${batchId}/dispense/labels`, body),
  dispenseMovement: (batchId: string, orderId: string, body: Record<string, unknown>) =>
    api.post(`/api/v1/batches/${batchId}/dispense/${orderId}/movements`, body),

  releaseGate: (batchId: string) => api.get<ReleaseGate>(`/api/v1/batches/${batchId}/release-gate`),
  releaseGates: () => api.get<ReleaseGate[]>("/api/v1/release-gates"),
  exceptionDashboard: () => api.get<ExceptionDashboard>("/api/v1/exceptions/dashboard"),
  exceptionReport: (batchId: string) =>
    api.get<ExceptionReport>(`/api/v1/batches/${batchId}/exception-report`),
  exceptions: (batchId: string, openOnly = false) =>
    api.get<ExceptionItem[]>(`/api/v1/batches/${batchId}/exceptions${openOnly ? "?open=true" : ""}`),
  reviewException: (batchId: string, exceptionId: string, body: Record<string, unknown>) =>
    api.post(`/api/v1/batches/${batchId}/exceptions/${exceptionId}/review`, body),
  reviewChecklist: (reviewType: string) =>
    api.get<{ item_code: string; item_text: string; mandatory: boolean }[]>(
      `/api/v1/release/review-checklists/${reviewType}`,
    ),
  recordReview: (batchId: string, body: Record<string, unknown>) =>
    api.post<RecordReviewResult>(`/api/v1/batches/${batchId}/reviews`, body),
  reviews: (batchId: string) => api.get<BatchReview[]>(`/api/v1/batches/${batchId}/reviews`),

  deviations: (batchId: string) => api.get<Deviation[]>(`/api/v1/batches/${batchId}/deviations`),
  raiseDeviation: (batchId: string, body: Record<string, unknown>) =>
    api.post(`/api/v1/batches/${batchId}/deviations`, body),
  investigateDeviation: (batchId: string, deviationId: string, body: Record<string, unknown>) =>
    api.post(`/api/v1/batches/${batchId}/deviations/${deviationId}/investigate`, body),
  closeDeviation: (batchId: string, deviationId: string, body: Record<string, unknown>) =>
    api.post(`/api/v1/batches/${batchId}/deviations/${deviationId}/close`, body),

  dispositions: (batchId: string) => api.get<BatchDisposition[]>(`/api/v1/batches/${batchId}/dispositions`),
  disposition: (batchId: string, body: Record<string, unknown>) =>
    api.post(`/api/v1/batches/${batchId}/disposition`, body),
  certify: (batchId: string, body: Record<string, unknown>) =>
    api.post(`/api/v1/batches/${batchId}/certification`, body),

  mbrTemplates: () => api.get<MbrTemplate[]>("/api/v1/mbr/templates"),
  mbrVersions: (templateId: string) => api.get<MbrVersion[]>(`/api/v1/mbr/templates/${templateId}/versions`),
  mbrVersion: (id: string) => api.get<MbrVersion>(`/api/v1/mbr/versions/${id}`),
  mbrStepLibrary: () => api.get<{ version: string; types: MbrStepType[] }>("/api/v1/mbr/step-library"),
  mbrSaveContent: (id: string, content: unknown) =>
    api.put<MbrVersion>(`/api/v1/mbr/versions/${id}/content`, { content }),
  createBatch: (body: Record<string, unknown>) => api.post<Batch>("/api/v1/batches", body),
  mbrTransition: (id: string, body: Record<string, unknown>) =>
    api.post<MbrVersion>(`/api/v1/mbr/versions/${id}/transition`, body),
  mbrCreateVersion: (templateId: string, body: Record<string, unknown>) =>
    api.post<MbrVersion>(`/api/v1/mbr/templates/${templateId}/versions`, body),
  mbrRedline: (id: string, against: string) =>
    api.get<{ markdown: string }>(`/api/v1/mbr/versions/${id}/redline?against=${against}`),
  setLimit: (body: Record<string, unknown>) => api.post<LimitVersion>("/api/v1/reference/limits", body),
  mbrAnalyze: (id: string) => api.post<MbrAnalysis>(`/api/v1/mbr/versions/${id}/analyze`),
  referenceLimits: () => api.get<LimitVersion[]>("/api/v1/reference/limits"),
  users: () => api.get<UserAccount[]>("/api/v1/users"),
  roles: () => api.get<Role[]>("/api/v1/roles"),
  weighingDevices: () => api.get<WeighingDevice[]>("/api/v1/weighing-devices"),
  accessReview: () => api.get<AccessReviewRow[]>("/api/v1/reports/access-review"),
  configBaseline: () => api.get<Record<string, unknown>>("/api/v1/config-baseline"),
  auditTrail: (query: string) => api.get<AuditPage>(`/api/v1/audit-trail?${query}`),
};

/**
 * AT-022: the complete trail as a file. A download needs the bearer token, which an <a href> cannot carry,
 * so the bytes are fetched here and handed to the browser as a blob. `query` carries the same filters as the
 * screen, minus paging — the file is everything that matches, not the page on screen.
 */
/**
 * The authenticated fetch for bodies the JSON helper cannot carry (a file upload, a file download). A refusal
 * comes back as the same ApiError every other call throws, so the screen shows the server's own sentence.
 */
export async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      headers: { ...(init.headers ?? {}), ...(token ? { authorization: `Bearer ${token}` } : {}) },
    });
  } catch {
    setReachable(false);
    throw new ApiError({ code: "NETWORK", message: NETWORK_MESSAGE, status: 0 });
  }
  setReachable(true);
  if (res.status === 401 && token !== null) {
    setToken(null);
    window.dispatchEvent(new Event(SESSION_ENDED));
  }
  if (!res.ok) {
    const text = await res.text();
    let e: Partial<ApiFailure> = {};
    try {
      e = ((text ? JSON.parse(text) : {}) as { error?: Partial<ApiFailure> }).error ?? {};
    } catch {
      e = {};
    }
    throw new ApiError({
      code: e.code ?? "UNKNOWN",
      message: e.message ?? `The server refused this request (${res.status}).`,
      status: res.status,
    });
  }
  return res;
}

export async function downloadAuditTrail(query: string, format: "json" | "csv"): Promise<void> {
  const res = await fetch(`${BASE}/api/v1/audit-trail/export?${query}&format=${format}`, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const text = await res.text();
    const e = ((text ? JSON.parse(text) : {}) as { error?: Partial<ApiFailure> }).error ?? {};
    throw new ApiError({
      code: e.code ?? "UNKNOWN",
      message: e.message ?? `The export was refused (${res.status}).`,
      status: res.status,
    });
  }
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = `audit-trail.${format}`;
  a.click();
  URL.revokeObjectURL(url);
}
